/*
 *
 * TurnReportList
 *
 */

import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TablePagination from '@mui/material/TablePagination';

import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import TurnReportListItem from '~/components/TurnReportListItem';
import { TABS } from './constants';
import messages from './messages';
import styles from './styles.module.css';
import { useTurnsWithoutReport } from '~/hooks/api/useTurnsWithoutReport';
import { useCreateTurnReport } from '~/hooks/api/useCreateTurnReport';
import { useTurnReports } from '~/hooks/api/useTurnReports';
import { TurnDto } from '~/generated';

type TabType = (typeof TABS)[keyof typeof TABS];

const getEmptyMessage = (tab: TabType | null) => {
  switch (tab) {
    case TABS.closed:
      return messages.noClosedTurnReports;
    case TABS.open:
      return messages.noOpenTurnReports;
    case TABS.missing:
    default:
      return messages.noMissingTurnReports;
  }
};

const route = getRouteApi('/turn-reports/');

export const TurnReportList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const search = route.useSearch();

  const activeTab = (search.tab as TabType) || TABS.closed;
  const currentPage = search.page ?? 0;
  const perPage = search.perPage ?? 20;

  // Fetch closed turn reports with pagination
  const shouldFetchClosedReports = activeTab === TABS.closed;
  const { data: closedReportsData, isLoading: isLoadingClosedReports } =
    useTurnReports(
      {
        query: {
          page: currentPage,
          perPage,
          filter: ['closed=true'],
          expand: [
            'turn.route',
            'turn.shift',
            'turn.language',
            'turn.turnAssignments',
          ],
        },
      },
      {
        enabled: shouldFetchClosedReports,
      }
    );

  const closedReports = closedReportsData?.turnReports || [];
  const totalCount = closedReportsData?.totalCount || 0;

  // Only fetch turns without report when not on the closed tab
  const shouldFetchTurns = activeTab !== TABS.closed;
  const { data: allTurnsWithoutReport = [], isLoading: isFetchingTurns } =
    useTurnsWithoutReport({
      enabled: shouldFetchTurns,
    });

  // Filter turns based on the active tab
  const getTurns = () => {
    if (activeTab === TABS.missing) {
      return allTurnsWithoutReport.filter((turn) => !turn.turnReport);
    } else if (activeTab === TABS.open) {
      return allTurnsWithoutReport.filter((turn) => turn.turnReport);
    }
    return [];
  };

  const turns = getTurns();

  const createTurnReportMutation = useCreateTurnReport();

  const isTab = (tab: TabType) => activeTab === tab;

  const createNavigateToReport = (reportId: number) => () => {
    navigate({
      to: '/turn-reports/$turnReportId',
      params: { turnReportId: String(reportId) },
      search: {
        from: `/turn-reports${search.tab ? `?tab=${search.tab}` : ''}${search.page !== undefined ? `${search.tab ? '&' : '?'}page=${search.page}` : ''}${search.perPage !== undefined ? `&perPage=${search.perPage}` : ''}`,
      },
    });
  };

  const createCreateReport = (turnId: number) => async () => {
    const response = await createTurnReportMutation.mutateAsync({ turnId });
    if (response.id) {
      createNavigateToReport(response.id)();
    }
  };

  const getEmptyListItem = () => (
    <ListItem data-test="empty-list-item">
      <ListItemText
        primary={
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            <FormattedMessage {...getEmptyMessage(search.tab as TabType)} />
          </Typography>
        }
      />
    </ListItem>
  );

  const onChangePage = (_event: unknown, page: number) => {
    // TablePagination is 0-indexed like our API
    navigate({
      to: '/turn-reports',
      search: {
        tab: TABS.closed,
        page,
        perPage,
      },
    });
  };

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newPerPage = parseInt(event.target.value, 10);
    navigate({
      to: '/turn-reports',
      search: {
        tab: TABS.closed,
        page: 0,
        perPage: newPerPage,
      },
    });
  };

  return (
    <ContentView
      header={
        <div>
          <AppBar position="static">
            <Toolbar>
              <DrawerButton />
              <Typography
                variant="h6"
                style={{ marginLeft: theme.spacing(2) }}
                sx={{
                  color: 'inherit',
                }}
              >
                <FormattedMessage {...messages.header} />
              </Typography>
            </Toolbar>
            <Tabs
              indicatorColor="secondary"
              textColor="inherit"
              onChange={(_event, tab: TabType) =>
                navigate({
                  to: '/turn-reports',
                  search: { tab, page: undefined, perPage: undefined },
                })
              }
              value={activeTab}
            >
              <Tab
                value={TABS.closed}
                label={<FormattedMessage {...messages.closedReports} />}
              />
              <Tab
                value={TABS.open}
                label={<FormattedMessage {...messages.openReports} />}
              />
              <Tab
                value={TABS.missing}
                label={<FormattedMessage {...messages.missingReports} />}
              />
            </Tabs>
          </AppBar>
        </div>
      }
      isLoading={isTab(TABS.closed) ? isLoadingClosedReports : isFetchingTurns}
    >
      {isTab(TABS.closed) && (
        <>
          {!isLoadingClosedReports && closedReports.length === 0 ? (
            getEmptyListItem()
          ) : (
            <>
              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage}
                rowsPerPage={perPage}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                className={styles['pagination-controls']}
              />
              <List>
                {closedReports.map((report) => {
                  const turnLead =
                    report.turn.turnAssignments?.find((a) => a.isLead)
                      ?.publisherMiniRecord ?? undefined;
                  return (
                    <TurnReportListItem
                      key={report.id}
                      showCompletedState={true}
                      isClosed={report.closed ?? false}
                      turn={report.turn}
                      turnLead={
                        turnLead
                          ? {
                              id: turnLead.id ?? 0,
                              firstName: turnLead.firstName ?? '',
                              middleName: turnLead.middleName ?? '',
                              lastName: turnLead.lastName ?? '',
                              email: turnLead.email ?? '',
                              phone: turnLead.phone ?? '',
                              languages: turnLead.languages ?? [],
                            }
                          : undefined
                      }
                      onClick={createNavigateToReport(report.id!)}
                    />
                  );
                })}
              </List>
            </>
          )}
        </>
      )}
      {!isTab(TABS.closed) && turns.length > 0 && (
        <List>
          {turns.map((turn) => {
            const turnLead =
              turn.turnAssignments?.find((a) => a.isLead)
                ?.publisherMiniRecord ?? undefined;
            const reportId =
              typeof turn.turnReport === 'object' && turn.turnReport?.id
                ? turn.turnReport.id
                : typeof turn.turnReport === 'number'
                  ? turn.turnReport
                  : null;
            return (
              <TurnReportListItem
                key={turn.id}
                showCompletedState={false}
                isClosed={false}
                turn={turn as TurnDto}
                turnLead={
                  turnLead
                    ? {
                        id: turnLead.id ?? 0,
                        firstName: turnLead.firstName ?? '',
                        middleName: turnLead.middleName ?? '',
                        lastName: turnLead.lastName ?? '',
                        email: turnLead.email ?? '',
                        phone: turnLead.phone ?? '',
                        languages: turnLead.languages ?? [],
                      }
                    : undefined
                }
                onClick={
                  reportId
                    ? createNavigateToReport(reportId)
                    : createCreateReport(turn.id!)
                }
              />
            );
          })}
        </List>
      )}
      {!isTab(TABS.closed) && !isFetchingTurns && turns.length === 0 && (
        <List>{getEmptyListItem()}</List>
      )}
    </ContentView>
  );
};

TurnReportList.displayName = 'TurnReportList';

export default TurnReportList;
